import { useEffect, useMemo, useState } from 'react'
import type { BotOut } from '../api'
import { mwh, price as fmtPrice, qty } from '../format'
import { BOT_KISA_TIP, TUM_KONTRATLAR, useBots } from '../useBots'
import { NewBotModal } from './NewBotModal'
import type { Contract } from '../types'

/**
 * Botlar sayfası — YALNIZ mobil. Masaüstünde bot listesi seçili kontratın
 * paneli içinde (`BotsTab`).
 *
 * Buradaki fark bilinçli: telefondaki asıl iş **gözetim** — "botlarım ne
 * durumda, biri hata mı verdi, birini durdurmam gerekiyor mu". O yüzden sayfa
 * TÜM kontratların botlarını birden gösteriyor.
 *
 * Liste **teslim gününe göre katlanır bölümlere** ayrılmış (kullanıcı isteği
 * 2026-08-03; öncesinde kontrat çipleriyle süzülüyordu). Gün = kontrat
 * anahtarındaki tarih, bot kimliğindeki kurulma tarihi DEĞİL: bir botun hangi
 * saatin tahtasında çalıştığı, ne zaman kurulduğundan daha çok işe yarıyor —
 * yarının kontratına bugün kurulan bot yarının altında durur. En yeni gün en
 * üstte ve açık gelir; gün içinde en yeni bot en üstte.
 *
 * Varsayılan görünüm yalnız ÇALIŞAN botlar. Duraklatılmış, kontratı kapanmış
 * ve kaldırılmış olanlar `Pasifler` anahtarının arkasında (kullanıcı kararı:
 * "aktif" = o an karar veren bot).
 *
 * Kart başına tek dokunuşla duraklat/sürdür — gezinmeden. Kaldırma onay
 * paneline bağlı: açık emirleri iptal ediyor ve geri alınamıyor.
 */

interface Props {
  /** Yeni bot modalının kontrat listesi (aktifleri kendisi süzüyor). */
  contracts: Contract[]
  /** Tahtada seçili kontrat — yeni bot için varsayılan. */
  selected: string | null
}

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]
const GUN_ADLARI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

/** Kontrat anahtarının teslim günü: `PH26080314` → `260803`. */
function gunAnahtari(kontrat: string): string {
  const govde = kontrat.slice(2, -2)
  return /^\d{6}$/.test(govde) ? govde : ''
}

/** `260803` → `Date`. Anahtar bozuksa `null` — satır listeden düşmesin. */
function gunTarihi(anahtar: string): Date | null {
  if (anahtar.length !== 6) return null
  const tarih = new Date(
    2000 + Number(anahtar.slice(0, 2)),
    Number(anahtar.slice(2, 4)) - 1,
    Number(anahtar.slice(4, 6)),
  )
  return Number.isNaN(tarih.getTime()) ? null : tarih
}

function gunEtiketi(anahtar: string): string {
  const tarih = gunTarihi(anahtar)
  if (!tarih) return 'Tarihsiz'
  return `${tarih.getDate()} ${AY_ADLARI[tarih.getMonth()]} · ${GUN_ADLARI[tarih.getDay()]}`
}

/** Bugünün gün anahtarı — başlıktaki `Bugün` rozeti için. */
function bugununAnahtari(): string {
  const t = new Date()
  const iki = (n: number) => String(n).padStart(2, '0')
  return `${iki(t.getFullYear() % 100)}${iki(t.getMonth() + 1)}${iki(t.getDate())}`
}

/** Çalışan bot = o an karar veren bot. Ötekiler `Pasifler`in arkasında. */
function calisiyor(bot: BotOut): boolean {
  return bot.active && !bot.expired && !bot.removed
}

export function BotsPage({ contracts, selected }: Props) {
  // Anahtar `useBots`e geçiyor: kapalıyken sunucu kaldırılmışlar için
  // veritabanına hiç gitmiyor (liste 3 sn'de bir yoklanıyor).
  const [pasifler, setPasifler] = useState(false)
  const { botlar, hata, mesgul, yenile, durumDegistir, kaldir } = useBots(
    TUM_KONTRATLAR, pasifler)
  //: `null` = kullanıcı hiçbir güne dokunmadı → en yeni gün açık. Dokunduktan
  //  sonra seçim onun; yeni bir gün gelse de kendiliğinden açılmaz.
  const [acikSecim, setAcikSecim] = useState<Set<string> | null>(null)
  const [modalAcik, setModalAcik] = useState(false)
  const [duzenlenen, setDuzenlenen] = useState<BotOut | null>(null)
  const [silinecek, setSilinecek] = useState<BotOut | null>(null)

  const calisanSayi = botlar.filter(calisiyor).length

  // Gruplama render'dan ayrı: liste 3 sn'de bir tazeleniyor, her turda yeniden
  // sıralamanın anlamı yok.
  const gunler = useMemo(() => {
    const gorunen = pasifler ? botlar : botlar.filter(calisiyor)
    const kova = new Map<string, BotOut[]>()
    for (const bot of gorunen) {
      const anahtar = gunAnahtari(bot.contract)
      const liste = kova.get(anahtar)
      if (liste) liste.push(bot)
      else kova.set(anahtar, [bot])
    }
    return [...kova.entries()]
      // En yeni gün en üstte; `YYMMDD` sözlük sıralı olduğu için düz kıyas
      // yetiyor. Bozuk anahtar (boş) en sona düşsün.
      .sort((a, b) => (b[0] || '').localeCompare(a[0] || ''))
      .map(([anahtar, liste]) => ({
        anahtar,
        // Gün içinde en yeni bot en üstte (kullanıcı isteği). Eşit zamanda
        // kimliğe düşülüyor ki sıra turdan tura oynamasın.
        botlar: [...liste].sort(
          (a, b) => b.createdAt - a.createdAt || b.id.localeCompare(a.id)),
        calisan: liste.filter(calisiyor).length,
      }))
  }, [botlar, pasifler])

  const bugun = bugununAnahtari()
  const ilkGun = gunler[0]?.anahtar
  const acik = acikSecim ?? new Set<string>(ilkGun != null ? [ilkGun] : [])
  const gunDegistir = (anahtar: string) => {
    const yeni = new Set(acik)
    if (yeni.has(anahtar)) yeni.delete(anahtar)
    else yeni.add(anahtar)
    setAcikSecim(yeni)
  }

  // Yeni bot hangi kontrata kurulacak: tahtada seçili olan, o da yoksa ilk açık
  // kontrat. Son basamak çıkmazı kapatıyor — uygulamayı açıp doğrudan Botlar'a
  // gelen kullanıcı, sebebi yazmayan kapalı bir düğmeyle karşılaşmasın.
  // Kontrat modal içinde zaten değiştirilebiliyor.
  const varsayilanKontrat = selected ?? contracts.find((c) => !c.closed)?.key ?? null

  useEffect(() => {
    if (!silinecek) return
    const kapat = (e: KeyboardEvent) => e.key === 'Escape' && setSilinecek(null)
    window.addEventListener('keydown', kapat)
    return () => window.removeEventListener('keydown', kapat)
  }, [silinecek])

  return (
    <main className="bots-sayfa">
      <div className="bots-ust">
        <span className="bots-say">
          <b>{calisanSayi}</b> çalışan bot
          {pasifler && botlar.length > calisanSayi && (
            <span className="muted"> · {botlar.length} toplam</span>
          )}
        </span>
        <button
          type="button"
          className="bots-yeni"
          onClick={() => setModalAcik(true)}
          disabled={!varsayilanKontrat}
        >
          + Yeni Bot
        </button>
      </div>

      <label
        className="switch bots-pasif"
        title="Duraklatılmış, kontratı kapanmış ve kaldırılmış botları göster/gizle"
      >
        <input
          type="checkbox"
          checked={pasifler}
          onChange={(e) => setPasifler(e.target.checked)}
        />
        <span className="switch-track" aria-hidden="true">
          <span className="switch-thumb" />
        </span>
        <span className="switch-text">Pasifler</span>
      </label>

      {hata && <div className="bots-error">{hata}</div>}

      {gunler.length === 0 ? (
        <div className="bots-bos">
          <span>{pasifler ? 'Hiç bot yok.' : 'Çalışan bot yok.'}</span>
          <span className="muted">
            {pasifler
              ? 'Bir şablon seçip parametreleriyle ilk botu kur.'
              : 'Duraklattığın ve kaldırdığın botlar için Pasifler anahtarını aç.'}
          </span>
        </div>
      ) : (
        <div className="bots-gunler">
          {gunler.map((gun) => {
            const acikMi = acik.has(gun.anahtar)
            return (
              <section className="bots-gun" key={gun.anahtar || 'tarihsiz'}>
                <button
                  type="button"
                  className={`bots-gun-bas${acikMi ? ' acik' : ''}`}
                  onClick={() => gunDegistir(gun.anahtar)}
                  aria-expanded={acikMi}
                >
                  <span className="bots-gun-ok" aria-hidden="true">
                    <svg viewBox="0 0 16 16" width="12" height="12">
                      <path d="M6 3.5 10.5 8 6 12.5" />
                    </svg>
                  </span>
                  <span className="bots-gun-ad">{gunEtiketi(gun.anahtar)}</span>
                  {gun.anahtar === bugun && <span className="bots-gun-bugun">Bugün</span>}
                  {/* İki sayı ancak farklıysa: "3/3" gürültü. */}
                  <span className="bots-gun-say">
                    {gun.calisan === gun.botlar.length
                      ? gun.botlar.length
                      : `${gun.calisan}/${gun.botlar.length}`}
                  </span>
                </button>

                {acikMi && (
                  <div className="bots-kartlar">
                    {gun.botlar.map((bot) => (
                      <BotKart
                        key={bot.id}
                        bot={bot}
                        mesgul={mesgul === bot.id}
                        onDurum={() => durumDegistir(bot)}
                        onDuzenle={() => setDuzenlenen(bot)}
                        onSil={() => setSilinecek(bot)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}

      {silinecek && (
        <div className="onay-perde" onClick={() => setSilinecek(null)}>
          <div
            className="onay-panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="onay-baslik"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="onay-baslik">Bot kaldırılsın mı?</h2>
            <p>
              <b>{silinecek.id}</b> kaldırılacak ve <b>açık emirleri iptal edilecek</b>. Kayıt
              geçmişi (dolumlar, olaylar) yerinde kalır.
            </p>
            <div className="onay-eylem">
              <button type="button" className="onay-vazgec" onClick={() => setSilinecek(null)}>
                Vazgeç
              </button>
              <button
                type="button"
                className="onay-onayla"
                autoFocus
                onClick={() => {
                  const bot = silinecek
                  setSilinecek(null)
                  kaldir(bot)
                }}
              >
                Kaldır
              </button>
            </div>
          </div>
        </div>
      )}

      {modalAcik && varsayilanKontrat && (
        <NewBotModal
          contract={varsayilanKontrat}
          contracts={contracts}
          onClose={() => setModalAcik(false)}
          onCreated={yenile}
        />
      )}
      {duzenlenen && (
        <NewBotModal
          contract={duzenlenen.contract}
          contracts={contracts}
          edit={duzenlenen}
          onClose={() => setDuzenlenen(null)}
          onCreated={yenile}
        />
      )}
    </main>
  )
}

function BotKart({
  bot,
  mesgul,
  onDurum,
  onDuzenle,
  onSil,
}: {
  bot: BotOut
  mesgul: boolean
  onDurum: () => void
  onDuzenle: () => void
  onSil: () => void
}) {
  // Kırmızı nokta kontratın kapanmasıdır — duraklatmanın grisinden ayrı: gri
  // "ben durdurdum", kırmızı "piyasa bitti" (geri açılamaz). Mavi BEST
  // botunun görevini bitirmesi: amount doldu, teklif atmıyor. Kaldırılmış bot
  // soluk: artık bir durumu değil, kaydı var.
  const durum = bot.removed
    ? 'kaldırıldı'
    : bot.expired
      ? 'kontrat kapandı'
      : bot.done ? 'miktar tamamlandı' : bot.active ? 'çalışıyor' : 'duraklatıldı'

  // Kaldırılmış bot yöneticinin belleğinde yok: duraklat/sürdür, düzenle ve
  // kaldır uçları 404 döner. Düğmeler kilitli, sebebi başlıkta — geçmişi
  // Kayıt Defteri > Bot Hareketleri'nde duruyor.
  const kilitli = mesgul || bot.removed

  return (
    <article className={`bot-kart${bot.lastError ? ' hatali' : ''}${bot.removed ? ' silinmis' : ''}`}>
      <div className="bk-ust">
        <span
          className={`bot-dot${
            bot.removed
              ? ' removed'
              : bot.expired ? ' expired' : bot.done ? ' done' : bot.active ? ' running' : ''
          }`}
          aria-hidden="true"
        />
        <span className="bk-id">{bot.id}</span>
        <span className="bk-tip">{BOT_KISA_TIP[bot.type] ?? bot.type}</span>
        {/* Net pozisyon MWh (kontrat listesindeki NetP ile aynı dil); tooltip
            lot köprüsü kuruyor. */}
        <span
          className={`bk-net${bot.net > 0 ? ' pos' : bot.net < 0 ? ' neg' : ''}`}
          title={`Alış ${qty(bot.bought)} / Satış ${qty(bot.sold)} lot`}
        >
          {bot.net === 0 ? '–' : `${bot.net > 0 ? '+' : ''}${mwh(bot.net)}`}
        </span>
      </div>

      <div className="bk-orta">
        <span className="bk-kontrat">{bot.contract}</span>
        <span className="bk-durum">{durum}</span>
      </div>

      <div className="bk-emirler">
        {bot.openOrders.length === 0 ? (
          <span className="muted">{bot.removed ? 'emirleri iptal edildi' : 'tahtada emri yok'}</span>
        ) : (
          bot.openOrders.map((o) => (
            <span key={o.id} className={`bot-quote ${o.side === 'BUY' ? 'buy' : 'sell'}`}>
              {o.side === 'BUY' ? 'A' : 'S'} {fmtPrice(o.price)} · {qty(o.quantity - o.filled)}
            </span>
          ))
        )}
      </div>

      {bot.lastError && <div className="bk-hata">{bot.lastError}</div>}

      <div className="bk-eylem">
        {/* Birincil eylem tam genişlik ve etiketli: telefonda botu durdurmak
            tek dokunuş olmalı, ikonu çözmeye çalışmadan. */}
        <button
          type="button"
          className={`bk-ana${bot.active ? '' : ' sur'}`}
          disabled={kilitli || bot.expired}
          onClick={onDurum}
        >
          {bot.removed
            ? 'Kaldırıldı'
            : bot.expired ? 'Kapandı' : bot.active ? '❙❙  Duraklat' : '▶  Sürdür'}
        </button>
        <button
          type="button"
          className="bk-ikon"
          disabled={kilitli}
          onClick={onDuzenle}
          aria-label={`${bot.id} botunu düzenle`}
          title={
            bot.removed
              ? 'Kaldırılmış bot düzenlenemez — geçmişi Kayıt Defteri’nde'
              : 'Düzenle — kimlik ve geçmiş korunur, strateji değişir'
          }
        >
          ✎
        </button>
        <button
          type="button"
          className="bk-ikon tehlike"
          disabled={kilitli}
          onClick={onSil}
          aria-label={`${bot.id} botunu kaldır`}
          title={bot.removed ? 'Bu bot zaten kaldırıldı' : 'Kaldır — açık emirleri iptal edilir'}
        >
          🗑
        </button>
      </div>
    </article>
  )
}
